import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'

// THE ONLY STYLESHEETS THE APP LOADS (PA-Z, 2026-09-23). The Practitioner App ships no CSS
// of its own: everything it paints comes from Signal DS, loaded ONCE, here, in the shape
// `scripts/check-no-css.mjs` pins.
//   tokens.css — the unlayered token values (:root). app.css below @imports the same file
//                itself (DS 1.67 `dist/app.css:6`), so this line is redundant on the cascade; it
//                stays because check-no-css.mjs pins it, pending the owner's ruling.
//   app.css    — the DS app entry: `@import "tailwindcss"` (this app's own Tailwind build runs
//                over it, scanning src/ for class names and the DS dist through its `@source`),
//                theme.css, fonts.css (incl. the Scrips icon font), components.css, the base
//                border/outline ink, the `#root` + `body` rules, the whole-app density contract
//                (`--app-density-zoom` / `--app-vh` / `--app-vw`, armed by the attribute set
//                below) and the `scrollbar-hide` utility.
// EXCEPTION (showcase look): two extra sheets load after app.css, see the import block below.
// One Tailwind bundle means one `@layer theme, base, components, utilities` order statement,
// which is what PROD-1598 needed (a second compiled bundle used to open `@layer utilities{`
// with no order statement and let utilities outrank preflight).
import '@scripsteam/scrips-signal-ds/tokens.css'
import '@scripsteam/scrips-signal-ds/app.css'
// Showcase look: the designer's contract and its bridge onto Signal DS names, unlayered and
// loaded after app.css so they win. Provisional, folds into Signal DS after the exhibition.
import './styles/contract.css'
import './styles/showcase-bridge.css'
import './styles/showcase-auth.css'
import './styles/showcase-billing.css'
import './styles/showcase-booking.css'
import './styles/showcase-calendar-week.css'
import './styles/showcase-calendar.css'
import './styles/showcase-chart.css'
import './styles/showcase-encounter-latest.css'
import './styles/showcase-patient.css'
import './styles/showcase-previsit.css'
import './styles/showcase-settings.css'
import './styles/showcase-signal.css'
import './styles/showcase-visit-note.css'
import './styles/showcase-wrap-up.css'
import { AppToaster } from './app/app-toaster'
import { applySignalLook } from './app/signal-look'
import { applyEncounterLatestLook } from './app/encounter-latest-look'
import { applyVisitNoteV2Look } from './app/visit-note-v2-look'
import { applyTemplateReviewLook } from './app/template-review-look'
import { isEncounterFlagOn } from './shared/lib/encounter-flags'
import App from './App'
import { queryClient } from './lib/query-client'
// Side effect: forgets the last person's patient data when a session ends (session-cleanup.ts).
import './app/session-cleanup'
// Must run before render so a crash during the first paint is still reported.
// No-ops unless VITE_SENTRY_DSN is set — see the header of that file for why
// absence of config deliberately means absence of transmission.
import { initObservability } from './observability/sentry'

initObservability()

document.documentElement.dataset.appDensity = ''
// Section 1 look (`?x=signal-look`): the attribute every rule of showcase-signal.css hangs on. Read once at start.
applySignalLook(isEncounterFlagOn('signalLook'))
// Section 4 note look (`?x=encounter-latest`): the attribute every rule of showcase-encounter-latest.css hangs on.
applyEncounterLatestLook(isEncounterFlagOn('encounterLatest'))
// The visit note as the catalog's encounter (`?x=visit-note-v2`): the attribute every rule of showcase-visit-note.css hangs on.
applyVisitNoteV2Look(isEncounterFlagOn('visitNoteV2'))
// Antonin's template review of 3 Nov (`?x=template-review`): the attribute its CSS parts hang on.
applyTemplateReviewLook(isEncounterFlagOn('templateReview'))

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {/*
        THE TOAST HOST WAS MISSING ENTIRELY, app-wide. `sonner`'s `toast.*` renders through a
        `<Toaster/>`; with none mounted, every call is a silent no-op. There was none anywhere in
        `src/` — verified 2026-09-07 by grep AND in the live DOM (no `[data-sonner-toaster]` node).
        Meanwhile the app calls it **289 times across 92 files**: `shared/lib/toast-helpers`
        (`showServerError` / `showSuccess` / `showWarning` — the entire error channel), plus
        billing, documents, scheduling, patients, settings and dashboard. Every one was silent, so
        API failures raised through `showServerError` vanished instead of surfacing.

        HOW IT HAPPENED: `toast-helpers.ts` says so in its own first line — "Ported from
        dev-scrips-pm-react src/shared/lib/toast-helpers.ts (PROD-1119)". The helpers were ported
        from PM; the `<Toaster/>` that makes them render was not. PM has always had it
        (`dev-scrips-pm-react/src/main.tsx:4,36`). Props were matched to PM's; since the showcase
        port (20 Oct) the presentation is the design's PaToast instead (`app/app-toaster.tsx`).

        Found while wiring the wrap-up sign gates, which refuse via `showWarning` and would
        otherwise have refused invisibly — strictly worse than the greyed button they replaced.
        This changes no call site; it lets the existing ones behave as written. Inside
        QueryClientProvider so anything the query layer raises is covered too.
      */}
      {/* Coloured, top centre, on the design tokens: `app/app-toaster.tsx`. */}
      <AppToaster />
    </QueryClientProvider>
  </StrictMode>,
)
